/**
 * Content bank API: modules and reading chapters of a 1:1 course or a cohort
 * batch, managed in one place. Uploading a chapter uses the reading-material
 * draft flow (create draft -> upload files -> parse -> upload missing -> publish).
 */

import api from "../../api";

const BANK = "api/content-bank";

export const getSources = async (type) =>
  (await api.get(`${BANK}/sources/`, { params: { type } })).data;

export const getCourse = async (courseId) =>
  (await api.get(`${BANK}/courses/${courseId}/`)).data;

// ─── Modules ────────────────────────────────────────────────────────────────

export const createModule = async (courseId, name, description = "") =>
  (await api.post(`${BANK}/courses/${courseId}/modules/`, { name, description })).data;

export const updateModule = async (moduleId, fields) =>
  (await api.patch(`${BANK}/modules/${moduleId}/`, fields)).data;

export const deleteModule = async (moduleId) =>
  (await api.delete(`${BANK}/modules/${moduleId}/`)).data;

export const restoreModule = async (moduleId) =>
  (await api.post(`${BANK}/modules/${moduleId}/restore/`)).data;

export const reorderModules = async (courseId, moduleIds) =>
  (await api.post(`${BANK}/courses/${courseId}/modules/reorder/`, { module_ids: moduleIds })).data;

export const copyChapters = async (moduleId, sourceModuleId) =>
  (await api.post(`${BANK}/modules/${moduleId}/copy-from/`, { source_module_id: sourceModuleId })).data;

// ─── Chapters ───────────────────────────────────────────────────────────────

export const updateChapter = async (chapterId, fields) =>
  (await api.patch(`${BANK}/chapters/${chapterId}/`, fields)).data;

export const deleteChapter = async (chapterId) =>
  (await api.delete(`${BANK}/chapters/${chapterId}/`)).data;

export const restoreChapter = async (chapterId) =>
  (await api.post(`${BANK}/chapters/${chapterId}/restore/`)).data;

export const moveChapter = async (chapterId, moduleId) =>
  (await api.post(`${BANK}/chapters/${chapterId}/move/`, { module_id: moduleId })).data;

export const reorderChapters = async (moduleId, chapterIds) =>
  (await api.post(`${BANK}/modules/${moduleId}/chapters/reorder/`, { chapter_ids: chapterIds })).data;

export const getPreviewUrl = async (chapterId) =>
  (await api.get(`${BANK}/chapters/${chapterId}/preview/`)).data.url;

export const getVersions = async (chapterId) =>
  (await api.get(`${BANK}/chapters/${chapterId}/versions/`)).data;

export const restoreVersion = async (chapterId, version) =>
  (await api.post(`${BANK}/chapters/${chapterId}/versions/${version}/restore/`)).data;

/** Download a chapter as "html" (zip package) or "md" (clean Markdown) and save it. */
export const downloadChapter = async (chapterId, as) => {
  const response = await api.get(`${BANK}/chapters/${chapterId}/download/`, {
    params: { as },
    responseType: "blob",
  });
  const disposition = response.headers["content-disposition"] || "";
  const match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  const filename = match ? decodeURIComponent(match[1]) : `chapter-${chapterId}.${as === "md" ? "md" : "zip"}`;
  const url = URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ─── Uploading (reading-material drafts) ────────────────────────────────────

export const createDraft = async (moduleId, files, { chapterId, chapterTitle } = {}) =>
  (await api.post("api/reading-material/drafts/create/", {
    topic_template_id: moduleId,
    chapter_id: chapterId || null,
    chapter_title: chapterTitle || "",
    files,
  })).data;

export const parseDraft = async (draftId, htmlS3Key) =>
  (await api.post(`api/reading-material/drafts/${draftId}/parse/`, { html_s3_key: htmlS3Key })).data;

export const getMissingUploadUrl = async (draftId, localPath, contentType) =>
  (await api.post(`api/reading-material/drafts/${draftId}/upload-missing/`, {
    local_path: localPath,
    content_type: contentType,
  })).data;

export const publishDraft = async (draftId) =>
  (await api.post(`api/reading-material/drafts/${draftId}/publish/`)).data;

export const discardDraft = async (draftId) => {
  await api.delete(`api/reading-material/drafts/${draftId}/`);
};

/** PUT a file to a signed upload URL, reporting progress (0-100). */
export const uploadFile = (uploadUrl, file, contentType, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && onProgress) onProgress(Math.round((event.loaded / event.total) * 100));
    });
    xhr.addEventListener("load", () =>
      xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)));
    xhr.addEventListener("error", () => reject(new Error("Network error during upload")));
    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.send(file);
  });

/** The API's error message, whatever shape it came in. */
export const errorMessage = (error, fallback = "Something went wrong") => {
  const data = error?.response?.data;
  if (typeof data === "string") return data;
  return data?.error || data?.detail || error?.message || fallback;
};
